import { useCallback, useEffect, useRef, useState } from 'react'

import { Modal } from 'antd'
import classNames from 'classnames'
import { useParams } from 'react-router'

import { Button } from 'common/components/Button/Button'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'
import { procedureApi, useFetchIntakeFormsQuery } from 'features/Procedure/state/procedure.api'
import { ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'
import { setIntakeBlocking } from 'features/Procedure/state/procedure.slice'
import {
  DocTemplateForm,
  type DocTemplateFormHandle,
} from 'features/Procedure/features/treatments/components/doc-template-preview-dialog/doc-template-form'

import { getEmrPinConfig } from './emr-pins'
import { clearIntakeLock, setIntakeLock } from './intake-lock'
import { IntakeFormsNav } from './intake-forms-nav'
import { IntakeSelfieStep, type IntakeSelfieStepHandle } from './intake-selfie-step'
import { PinPromptModal } from './pin-prompt-modal'
import { useBlockPageExit } from './use-block-page-exit'
import { useIntakeFormsNavigation } from './use-intake-forms-navigation'
import styles from './intake-forms-dialog.module.scss'

type IntakeFormsDialogProps = {
  isOpen: boolean
  onClose: () => void
  patientId: number
}

const SKELETON_FIELD_KEYS = ['f1', 'f2', 'f3', 'f4', 'f5']

export const IntakeFormsDialog = ({ isOpen, onClose, patientId }: IntakeFormsDialogProps) => {
  const { siteId } = useParams<{ siteId: string }>()
  const dispatch = useAppDispatch()
  const encounterUid = useAppSelector((state) => state.procedureReducer.selectedEncounterUid)
  const { data: profile } = useFetchProfileQuery(null)
  const { pins, pinLength } = getEmrPinConfig(profile?.profile)

  const { currentData, isFetching } = useFetchIntakeFormsQuery(
    { siteId: siteId!, patientId },
    { skip: !isOpen || !siteId || !patientId },
  )
  const forms = currentData?.forms
  const hasProfileImage = currentData?.hasProfileImage ?? true

  const nav = useIntakeFormsNavigation(forms)

  const [isPinOpen, setPinOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)
  const [finishing, setFinishing] = useState(false)
  const [photoDismissed, setPhotoDismissed] = useState(false)
  const formRef = useRef<DocTemplateFormHandle>(null)
  const selfieRef = useRef<IntakeSelfieStepHandle>(null)

  useBlockPageExit(isOpen)

  useEffect(() => {
    dispatch(setIntakeBlocking(isOpen))
    return () => {
      dispatch(setIntakeBlocking(false))
    }
  }, [isOpen, dispatch])

  // Persist the block so a refresh/route change cannot escape the intake.
  useEffect(() => {
    if (isOpen && patientId) {
      setIntakeLock({ patientId, encounterUid: encounterUid ?? null })
    }
  }, [isOpen, patientId, encounterUid])

  // Reset transient completion state so a closed/reopened dialog never keeps a stuck Done spinner.
  useEffect(() => {
    setPhotoDismissed(false)
    setFinishing(false)
  }, [patientId, isOpen])

  const isBusy = isFetching || busy
  const isComplete = nav.status === 'complete'
  const currentTitle =
    nav.status === 'active' && forms
      ? forms.find((form) => form.id === nav.currentFormId)?.title ?? ''
      : ''
  const navTitle = isComplete ? 'Intake forms' : currentTitle

  // The only way out is the staff PIN; without a configured PIN there is nothing to gate on.
  const requestPinClose = useCallback(() => {
    if (pins.length === 0) {
      clearIntakeLock()
      onClose()
      return
    }
    setPinOpen(true)
  }, [pins.length, onClose])

  const handleDone = useCallback(async () => {
    if (finishing) return
    setFinishing(true)
    const uploaded = selfieRef.current ? await selfieRef.current.commit() : true
    if (!uploaded) {
      setFinishing(false)
      return
    }
    dispatch(
      procedureApi.util.invalidateTags([
        { type: ProcedureStoreTagTypes.IntakeForms, id: patientId },
      ]),
    )
    setFinishing(false)
    requestPinClose()
  }, [finishing, dispatch, patientId, requestPinClose])

  const handlePinCancel = useCallback(() => {
    setPinOpen(false)
  }, [])

  const handleVerified = useCallback(() => {
    clearIntakeLock()
    setPinOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      classNames={{
        content: styles.modalContent,
        wrapper: styles.modalWrapper,
        body: styles.modalBody,
      }}
      open={isOpen}
      closable={false}
      maskClosable={false}
      keyboard={false}
      closeIcon={null}
      footer={null}
      title={null}
      destroyOnHidden
      style={{ top: 20 }}>
      <div className={styles.container}>
        <IntakeFormsNav
          title={navTitle}
          currentIndex={nav.currentIndex < 0 ? 0 : nav.currentIndex}
          total={nav.total}
          canPrev={nav.canPrev}
          canNext={nav.canNext}
          disabled={isBusy}
          onPrev={nav.goPrev}
          onNext={nav.goNext}
        />

        <div className={styles.content}>
          {nav.status === 'loading' && (
            <div className={styles.formSkeleton} aria-hidden='true'>
              <span className={classNames(styles.skeletonBar, styles.skeletonHeading)} />
              {SKELETON_FIELD_KEYS.map((key) => (
                <div key={key} className={styles.skeletonField}>
                  <span className={classNames(styles.skeletonBar, styles.skeletonLabel)} />
                  <span className={classNames(styles.skeletonBar, styles.skeletonInput)} />
                </div>
              ))}
            </div>
          )}

          {isComplete && (
            <div className={styles.completeWrapper}>
              {!hasProfileImage && siteId && !photoDismissed && (
                <IntakeSelfieStep
                  ref={selfieRef}
                  siteId={siteId}
                  patientId={patientId}
                  onRemove={() => setPhotoDismissed(true)}
                />
              )}
            </div>
          )}

          {nav.status === 'active' && nav.currentFormId !== null && encounterUid && (
            <DocTemplateForm
              key={nav.currentFormId}
              ref={formRef}
              embedded
              patientId={patientId}
              encounterUid={encounterUid}
              templateId={nav.currentFormId}
              onSubmitSuccess={(signedId) => nav.handleSigned(signedId)}
              onBusyChange={setBusy}
              onReadyChange={setReady}
            />
          )}
        </div>

        <div className={styles.footer}>
          {isComplete ? (
            <Button onClick={handleDone} loading={finishing} disabled={finishing}>
              Done
            </Button>
          ) : (
            <>
              <Button
                modifier={BUTTON_MODIFIER.DEFAULT}
                onClick={requestPinClose}
                disabled={isBusy}>
                Close
              </Button>
              {nav.status === 'active' && (
                <Button
                  onClick={() => formRef.current?.submit()}
                  loading={isBusy}
                  disabled={isBusy || !ready}>
                  {nav.canNext ? 'Next' : 'Submit'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <PinPromptModal
        isVisible={isPinOpen}
        emrPins={pins}
        pinLength={pinLength}
        onCancel={handlePinCancel}
        onVerified={handleVerified}
      />
    </Modal>
  )
}
